import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IUsuarioTable } from '../../models/IUsuario';
import { IFormError, IFormProps } from '../utils';
import { validateEmail } from '../../../utils';
import { ICliente, ITipoUsuario } from '../../models';

interface ICreateUsuarioFormFields {
  nome: string
  email: string
  login: string
  idCliente: number
  idPerfil: number
  tipoUsuario?: ITipoUsuario
}

interface IEditUsuarioFormFields {
  idUsuario: number
  nome: string
  email: string
  ativo: boolean
  idCliente: number
  login: string
  idPerfil?: number
  tipoUsuario?: ITipoUsuario
}

const createUsuarioFormFieldsInitialState: ICreateUsuarioFormFields = {
  nome: '',
  email: '',
  login: '',
  idCliente: 0,
  idPerfil: 0
};


const editUsuarioFormFieldsInitialState: IEditUsuarioFormFields = {
  ativo: true,
  email: '',
  idCliente: 0,
  idUsuario: 0,
  login: '',
  nome: ''
};

interface IUsuariosState {
  usuarios: Omit<IUsuarioTable, "Senha">[];
  usuariosTotal: number;
  usuariosPagination: {
    page: number;
    limit: number;
  };
  createUsuarioForm: IFormProps<ICreateUsuarioFormFields>;
  editUsuarioForm: IFormProps<IEditUsuarioFormFields>;
  clienteSelecionado?: ICliente
}

const initialState: IUsuariosState = {
  usuarios: [],
  usuariosTotal: 0,
  usuariosPagination: {
    page: 0,
    limit: 10
  },
  createUsuarioForm: {
    fields: createUsuarioFormFieldsInitialState,
    errors: [],
    touched: false,
    delivered: false
  },
  editUsuarioForm: {
    fields: editUsuarioFormFieldsInitialState,
    errors: [],
    touched: false,
    delivered: false
  }
};

export const validateCreateUsuarioForm = (fields: ICreateUsuarioFormFields): IFormError<keyof ICreateUsuarioFormFields>[] => {
  const errors: IFormError<keyof ICreateUsuarioFormFields>[] = [];

  if (!fields.nome) {
    errors.push({
      key: 'nome',
      message: 'Nome é obrigatório',
    });
  }

  if (!fields.email) {
    errors.push({
      key: 'email',
      message: 'Email é obrigatório',
    });
  } else {
    if (!validateEmail(fields.email)) {
      errors.push({
        key: 'email',
        message: 'Email inválido',
      });
    }
  }

  if (!fields.login) {
    errors.push({
      key: 'login',
      message: 'Login é obrigatório',
    });
  }

  if (!fields.idPerfil) {
    errors.push({
      key: 'idPerfil',
      message: 'Perfil é obrigatório',
    });
  }

  return errors;
}

export const validateEditUsuarioForm = (fields: IEditUsuarioFormFields): IFormError<keyof IEditUsuarioFormFields>[] => {
  const errors: IFormError<keyof IEditUsuarioFormFields>[] = [];

  if (!fields.nome) {
    errors.push({
      key: 'nome',
      message: 'Nome é obrigatório',
    });
  } else if (!fields.idCliente || fields.idCliente === 0) {
    errors.push({
      key: 'idCliente',
      message: 'O usuário deve estar associado a um cliente'
    })
  } else if (!fields.idUsuario || fields.idUsuario === 0) {
    errors.push({
      key: 'idUsuario',
      message: 'Selecione um usuário primeiro'
    })
  }

  if (!fields.email) {
    errors.push({
      key: 'email',
      message: 'E-mail é obrigatório',
    });
  } else {
    if (!validateEmail(fields.email)) {
      errors.push({
        key: 'email',
        message: 'E-mail inválido',
      });
    }
  }

  if (!fields.idPerfil) {
    errors.push({
      key: 'idPerfil',
      message: 'Vincule o usuário a um perfil'
    })
  }

  return errors;
}

const usuariosSlice = createSlice({
  name: 'Usuarios',
  initialState,
  reducers: {
    // Lists Actions
    setUsuariosState(state, action: PayloadAction<{ usuarios: Omit<IUsuarioTable, "Senha">[] }>) {
      state.usuarios = action.payload.usuarios;
    },
    setUsuariosTotalState(state, action: PayloadAction<{ total: number }>) {
      state.usuariosTotal = action.payload.total;
    },
    setUsuariosPaginationState(state, action: PayloadAction<{ page: number; limit: number }>) {
      state.usuariosPagination = {
        page: action.payload.page,
        limit: action.payload.limit
      };
    },
    // Create Usuario Actions
    setCreateUsuarioFormNomeState(state, action: PayloadAction<{ nome: string }>) {
      state.createUsuarioForm.fields.nome = action.payload.nome;
    },
    setCreateUsuarioFormEmailState(state, action: PayloadAction<{ email: string }>) {
      state.createUsuarioForm.fields.email = action.payload.email;
    },
    setCreateUsuarioFormLoginState(state, action: PayloadAction<{ login: string }>) {
      state.createUsuarioForm.fields.login = action.payload.login;
    },
    setCreateUsuarioFormIdPerfilState(state, action: PayloadAction<{ idPerfil: number }>) {
      state.createUsuarioForm.fields.idPerfil = action.payload.idPerfil;
    },
    setCreateUsuarioFormTipoUsuarioState(state, action: PayloadAction<{ tipoUsuario: ITipoUsuario }>) {
      state.createUsuarioForm.fields.tipoUsuario = action.payload.tipoUsuario;
    },
    setCreateUsuarioFormIdClienteState(state, action: PayloadAction<{ idCliente: number }>) {
      state.createUsuarioForm.fields.idCliente = action.payload.idCliente;
    },
    setCreateUsuarioFormTouchedState(state, action: PayloadAction<{ touched: boolean }>) {
      state.createUsuarioForm.touched = action.payload.touched;
    },
    setCreateUsuarioFormErrorsState(state, action: PayloadAction<{ errors: IFormError<keyof ICreateUsuarioFormFields>[] }>) {
      state.createUsuarioForm.errors = action.payload.errors;
    },
    setCreateUsuarioFormDeliveredState(state, action: PayloadAction<{ delivered: boolean }>) {
      state.createUsuarioForm.delivered = action.payload.delivered;
    },
    resetCreateUsuarioFormState(state) {
      state.createUsuarioForm = {
        fields: createUsuarioFormFieldsInitialState,
        errors: [],
        touched: false,
        delivered: false
      };
    },
    // Edit Usuario Actions
    setEditUsuarioFormIdUsuarioState(state, action: PayloadAction<{ idUsuario: number }>) {
      state.editUsuarioForm.fields.idUsuario = action.payload.idUsuario;
    },
    setEditUsuarioFormNomeState(state, action: PayloadAction<{ nome: string }>) {
      state.editUsuarioForm.fields.nome = action.payload.nome;
    },
    setEditUsuarioFormLoginState(state, action: PayloadAction<{ login: string }>) {
      state.editUsuarioForm.fields.login = action.payload.login;
    },
    setEditUsuarioFormEmailState(state, action: PayloadAction<{ email: string }>) {
      state.editUsuarioForm.fields.email = action.payload.email;
    },
    setEditUsuarioFormAtivoState(state, action: PayloadAction<{ ativo: boolean }>) {
      state.editUsuarioForm.fields.ativo = action.payload.ativo;
    },
    setEditUsuarioFormIdClienteState(state, action: PayloadAction<{ idCliente: number }>) {
      state.editUsuarioForm.fields.idCliente = action.payload.idCliente;
    },
    setEditUsuarioFormIdPerfilState(state, action: PayloadAction<{ idPerfil: number }>) {
      state.editUsuarioForm.fields.idPerfil = action.payload.idPerfil;
    },
    setEditUsuarioFormTipoUsuarioState(state, action: PayloadAction<{ tipoUsuario: ITipoUsuario }>) {
      state.editUsuarioForm.fields.tipoUsuario = action.payload.tipoUsuario;
    },
    setEditUsuarioFormTouchedState(state, action: PayloadAction<{ touched: boolean }>) {
      state.editUsuarioForm.touched = action.payload.touched;
    },
    setEditUsuarioFormErrorsState(state, action: PayloadAction<{ errors: IFormError<keyof IEditUsuarioFormFields>[] }>) {
      state.editUsuarioForm.errors = action.payload.errors;
    },
    setEditUsuarioFormDeliveredState(state, action: PayloadAction<{ delivered: boolean }>) {
      state.editUsuarioForm.delivered = action.payload.delivered;
    },
    resetEditUsuarioFormState(state) {
      state.editUsuarioForm = {
        fields: editUsuarioFormFieldsInitialState,
        errors: [],
        touched: false,
        delivered: false
      };
    },
    setClienteSelecionadoState(state, action: PayloadAction<{
      cliente: ICliente
    }>) {
      state.clienteSelecionado = action.payload.cliente
    },
    setResetClienteSelecionadoState(state) {
      state.clienteSelecionado = initialState.clienteSelecionado
    },
  },
});

export const {
  //LISTS ACTIONS
  setUsuariosState,
  setUsuariosPaginationState,
  setUsuariosTotalState,
  // CREATE USUARIO ACTIONS
  setCreateUsuarioFormNomeState,
  setCreateUsuarioFormEmailState,
  setCreateUsuarioFormLoginState,
  setCreateUsuarioFormIdPerfilState,
  setCreateUsuarioFormIdClienteState,
  setCreateUsuarioFormTipoUsuarioState,
  setCreateUsuarioFormTouchedState,
  setCreateUsuarioFormErrorsState,
  setCreateUsuarioFormDeliveredState,
  resetCreateUsuarioFormState,
  // EDIT USUARIO ACTIONS
  setEditUsuarioFormIdUsuarioState,
  setEditUsuarioFormNomeState,
  setEditUsuarioFormEmailState,
  setEditUsuarioFormAtivoState,
  setEditUsuarioFormIdClienteState,
  setEditUsuarioFormIdPerfilState,
  setEditUsuarioFormTipoUsuarioState,
  setEditUsuarioFormTouchedState,
  setEditUsuarioFormErrorsState,
  setEditUsuarioFormDeliveredState,
  resetEditUsuarioFormState,
  setEditUsuarioFormLoginState,
  // Cliente selecionado
  setClienteSelecionadoState,
  setResetClienteSelecionadoState
} = usuariosSlice.actions;

export const usuariosReducer = usuariosSlice.reducer;